import React, { useMemo, useRef } from 'react';
import { map, pickBy, range, reverse } from 'ramda';
import { animated, useSpring } from 'react-spring'
import styled from 'styled-components';
import Sadko from '../../service/Sadko';
import withTune from '../hocs/withTune';

const defaultNoteOffset = 80;
const notesMargin = 100;
const defaultStringOffset = 5;

const Container = styled.div`
    margin: 0 auto;
    overflow: hidden;
`;

const TabsContent = styled(animated.div)`
    overflow-x: auto;
    margin: 0 auto;
`;

const TabRowContainer = styled.div`
    padding-bottom: 24px;
`;

const bevelWidth = 30;
const Line = styled.div`
    height: 8px;
    border-bottom: 1px solid #333;
    width: ${({ maxWidth, position }) => `${maxWidth - (bevelWidth + defaultStringOffset * position) * 2}px`};
    margin: 8px ${({ position }) => `${bevelWidth + defaultStringOffset * position}px;`};
`;

const LineContainer = styled.div`
    position: relative;
`;

const ChordName = styled.div`
    position: absolute;
    text-align: center;
    font-size: 13px;
    width: 26px;
    color: ${({ active }) => `${active ? 'red' : 'black'}`}
`;

const LineName = styled.div`
    position: absolute;
    margin-top: -5px;
    left: ${({ position }) => `${defaultStringOffset * position + 12}px;`};
`;

const ChordsLineContainer = styled.div`
    position: relative;
    height: 10px;
`;

const MutedNote = styled.div`
    position: absolute;
    top: 2px;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: ${({ active }) => `${active ? 'black' : '#333'}`};
`;

const PlayedNote = styled(MutedNote)`
    display: ${({ alwaysShow, active }) => `${alwaysShow || active ? 'block' : 'none'}`};
    background: ${({ active }) => `${active ? '#aa0000' : '#ffffff'}`};
    border: 1px solid #aa0000;
`;

const getSongPositionOffset = (position, notesTimeMap) => {
    if (notesTimeMap) {
        position = notesTimeMap[position];
    }

    return defaultNoteOffset + position * notesMargin;
};

const getPartNotes = (notes, part, rowTime, lastRow = false) => {
    const timeRange = {
        start: part * rowTime,
        end: (part + 1) * rowTime,
    };

    return map((stringNotes) => stringNotes.filter((note) => note.time >= timeRange.start && (!lastRow ? note.time < timeRange.end : true)), notes);
};

const getPartChords = (partChords, part, rowTime, lastRow) => {
    const start = part * rowTime;
    const timeRange = {
        start: start,
        end: start + rowTime,
    };

    return pickBy((notes, time) => time >= timeRange.start && (lastRow ? time <= timeRange.end : time < timeRange.end), partChords);
};

const ChordsLine = ({ chords, songPosition, notesTimeMap, tune }) => {
    return <ChordsLineContainer>
        {Object.keys(chords)
            .map((position, index) => <ChordName
                    key={`chord-${index}`}
                    active={songPosition === parseFloat(position)}
                    style={{ left: getSongPositionOffset(position, notesTimeMap) - 5 + 'px' }}>
                    {Sadko.transposeNoteByTune(chords[position], tune)}
                </ChordName>
            )}
    </ChordsLineContainer>
};

const StringNote = ({ number, note, mutedNotes, songPosition, notesTimeMap, maxWidth, playedNotes, song }) => {
    return <LineContainer key={`line-${number}`}>
        <LineName position={number}>{note}</LineName>
        <Line position={number} maxWidth={maxWidth}>
            {mutedNotes.map((note, index) => <MutedNote
                key={`note-${index}`}
                active={songPosition === note.time}
                style={{ left: getSongPositionOffset(note.time, notesTimeMap) + 'px' }}/>
            )}
            {playedNotes.map((note, index) => <PlayedNote
                alwaysShow={song.needShowPlayableNotes()}
                key={`played-note-${index}`}
                active={songPosition === note.time}
                style={{ left: getSongPositionOffset(note.time, notesTimeMap) + 'px' }}/>
            )}
        </Line>
    </LineContainer>
};

function PlayField({ song, track, tune, songPosition = 0, stringsCount = 9, rowCapacity = 24 }) {
    const noteTimeStep = 0.25;
    const originTune = 'G';

    const screenWidth = typeof window === 'undefined' ? 1024 : window.innerWidth;

    const tabsContentRef = useRef(null);

    const timeBlocks = useMemo(() => {
        const result = {};

        track.notes.forEach((note) => {
            if (!(note.time in result)) {
                result[note.time] = [];
            }

            result[note.time].push(note);
        });

        return result;
    }, [track]);

    const timeSequence = useMemo(() => {
        const steps = track.notes
            .map((note) => note.time)
            .sort((time1, time2) => time1 > time2 ? 1 : -1);

        return steps.filter((time, index, self) => self.indexOf(time) === index);

    }, [track]);

    const countRows = Math.ceil(Object.keys(timeBlocks).length / rowCapacity);
    const songDuration = Math.max(...Object.keys(timeBlocks));
    const rowTime = songDuration / countRows;

    const notesTimeMap = useMemo(() => {
        if (countRows <= 1) {
            return undefined;
        }

        let time = 0;
        const result = {};
        let currentRow = 0;

        for (const currentTime of timeSequence) {
            const row = Math.floor(currentTime / rowTime);
            if (currentRow !== row && parseFloat(currentTime) !== songDuration) {
                time = 0;
                currentRow = row;
            }

            result[currentTime] = time;
            time += noteTimeStep;
        }

        return result;
    }, [songDuration, rowTime, countRows, timeSequence, tune]);

    const maxRowTime = useMemo(() => {
        if (!notesTimeMap) {
            return rowTime;
        }

        let currentTime = 0;
        for (const time of timeSequence) {
            if (notesTimeMap[time] === 0 && currentTime > 0) {
                return currentTime;
            }
            currentTime = time;
        }
    }, [rowTime, notesTimeMap, timeSequence]);

    const timeChords = useMemo(() => {
        let result = {};

        timeSequence.forEach((time) => {
            const notes = timeBlocks[time];
            const chord = Sadko.guessChordName(notes.map((note) => note.name));

            if (chord) {
                result[time] = chord;
            }
        });

        if (Object.keys(result).length <= 0) {
            const predefinedChords = song.getChordsLine();
            if (!!predefinedChords && typeof predefinedChords === 'object') {
                result = predefinedChords;
            }
        }

        return result;

    }, [timeBlocks, timeSequence, song, tune]);

    const strings = useMemo(() => reverse(range(1, stringsCount + 1)), [stringsCount]);

    const notesMap = useMemo(() => {
        const result = {};
        strings.forEach((stringNumber) => {
            result[stringNumber] = Sadko.getStringNote(stringNumber, tune, false);
        });

        return result;
    }, [strings, tune]);

    const playedStringNotes = useMemo(() => {
        const result = {};
        strings.forEach((stringNumber) => {
            result[stringNumber] = [];
        });

        Object.keys(timeBlocks).forEach((position) => {
            const notes = timeBlocks[position];
            notes.forEach((note) => result[Sadko.getNoteString(note.name, originTune)].push({
                name: note.name,
                time: parseFloat(position),
            }));
        });

        return result;
    }, [stringsCount, strings, timeBlocks, tune]);

    const mutedStringNotes = useMemo(() => {
        const result = {};

        strings.forEach((stringNumber) => {
            result[stringNumber] = [];
        });

        if (!song.needShowMutedNotes()) {
            return result;
        }

        Object.keys(timeChords).forEach((position) => {
            const chord = timeChords[position];

            const notes = Sadko.getMutesChordNotes(chord, stringsCount, originTune);

            notes.forEach((note) => result[Sadko.getNoteString(note, originTune)].push({
                name: Sadko.transposeNoteByTune(note, tune),
                time: parseFloat(position),
            }));
        });

        return result;
    }, [stringsCount, strings, timeChords, tune]);

    const fieldWidth = useMemo(
        () => getSongPositionOffset(maxRowTime, notesTimeMap) + getSongPositionOffset(0) + defaultStringOffset,
        [maxRowTime, notesTimeMap]
    );

    const tabsContentWidth = fieldWidth > screenWidth ? screenWidth - defaultStringOffset * 2 - 32 : fieldWidth;
    const tabsContentCenter = tabsContentWidth ? tabsContentWidth / 2 : 0;

    const currentNoteOffset = getSongPositionOffset(songPosition, notesTimeMap);

    const scrollLeft = tabsContentRef.current ? tabsContentRef.current.scrollLeft : 0;
    const nextScrollPosition = currentNoteOffset > tabsContentCenter ? currentNoteOffset - tabsContentCenter : 0;

    const scrollProps = useSpring({ scrollLeft: nextScrollPosition, from: { scrollLeft: scrollLeft } });

    const parts = range(0, countRows);

    return <Container>
        <TabsContent ref={tabsContentRef} style={{ width: `${tabsContentWidth}px` }} scrollLeft={scrollProps.scrollLeft}>
            {parts.map((part) => {
                const mutedPartNotes = getPartNotes(mutedStringNotes, part, rowTime, part === parts.length - 1);
                const playedNotes = getPartNotes(playedStringNotes, part, rowTime, part === parts.length - 1);
                const partChords = getPartChords(timeChords, part, rowTime, part === parts.length - 1);

                return <TabRowContainer key={`part-${part}`}>
                    {strings.map((stringNumber) => <StringNote
                        song={song}
                        key={`part-${part}-line-${stringNumber}`}
                        note={notesMap[stringNumber]}
                        number={stringNumber}
                        tune={tune}
                        rowNumber={part}
                        rowsCount={parts.length}
                        maxWidth={fieldWidth}
                        rowTime={rowTime}
                        notesTimeMap={notesTimeMap}
                        mutedNotes={mutedPartNotes[stringNumber]}
                        playedNotes={playedNotes[stringNumber]}
                        songPosition={songPosition}
                    />)}
                    <ChordsLine
                        tune={tune}
                        chords={partChords}
                        notesTimeMap={notesTimeMap}
                        songPosition={songPosition}
                    />
                </TabRowContainer>
            })}
        </TabsContent>
    </Container>
}

export default withTune(PlayField);
